import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { catchError, debounceTime, of, Subject, takeUntil } from 'rxjs';
import * as moment from 'moment';
import { CopyItem, CopySection } from './copy-management.interfaces';
import { EditCopyDialogComponent } from '../dialogs/edit-copy-dialog/edit-copy-dialog.component';
import { DATE_SLASH_SHORT_FORMAT } from '../../core/formatting/date-formatting';
import { SelectOption } from '../../shared/components/common-single-selector/common-single-selector.component';
import { CopyService } from '../../services/copy.service';
import { SnackbarService } from '../../services/snackbar.service';

@Component({
  selector: 'app-copy-management',
  templateUrl: './copy-management.component.html',
  styleUrls: ['./copy-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopyManagementComponent implements OnInit, OnDestroy {
  // Namespace management
  selectedNamespaceId: CopySection | 'all' = 'all';
  namespaceOptions: SelectOption<CopySection | 'all'>[] = [
    { key: 'all', value: 'Összes kiválasztva' },
    { key: 'AUTH_PROFILE', value: 'Bejelentkezés és fiókkezelés' },
    { key: 'FIRST_RENT_CHECKOUT', value: 'Első bérlő checkout' },
    { key: 'OTHER_PAGES_COMMON', value: 'Egyéb oldalak' },
    { key: 'RENT', value: 'Bérlés' },
    { key: 'SIGNED_IN_USER_CHECKOUT', value: 'Bejelentkezett felhasználó fizetése' },
  ];

  // Table configuration
  displayedColumns = ['copyKey', 'hungarian', 'english', 'lastModified', 'actions'];
  dataSource = new MatTableDataSource<CopyItem>([]);

  // Pagination
  pageSizeOptions = [10, 20, 50];
  pageSize = 10;
  pageIndex = 0;
  dataLength = 0;

  // Search
  searchTerm = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Loading state
  loading = false;

  // Constants
  FLAG_HU = '🇭🇺';
  FLAG_GB = '🇬🇧';

  constructor(
    private dialog: MatDialog,
    private copyService: CopyService,
    private snackbarService: SnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.pageIndex = 0;
      this.loadData();
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNamespaceChange(): void {
    this.pageIndex = 0;
    this.loadData();
  }

  applyFilter(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onPagination(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.cdr.markForCheck();

    const searchQuery = this.searchTerm.trim() || undefined;
    const section = this.selectedNamespaceId === 'all' ? undefined : this.selectedNamespaceId;

    this.copyService
      .getCopies$(this.pageSize, this.pageIndex, searchQuery, section)
      .pipe(
        catchError(() => {
          this.snackbarService.error('A copy elemek lekérése során hiba lépett fel!');
          this.loading = false;
          this.cdr.markForCheck();
          return of();
        }),
      )
      .subscribe((copyBatch) => {
        this.dataSource.data = copyBatch.items;
        this.dataLength = copyBatch.totalCount;
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  openEditDialog(item: CopyItem): void {
    const dialogRef = this.dialog.open(EditCopyDialogComponent, {
      data: { copyKey: item.copyKey },
      width: '1200px',
      maxWidth: '95vw',
      autoFocus: false,
      panelClass: 'white-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.loadData();
      }
    });
  }

  formatDate(date: string): string {
    return moment(date).format(DATE_SLASH_SHORT_FORMAT);
  }
}
